import { createContext, useContext, useState, useEffect } from 'react';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';
import { authedApiFetch } from 'utils/Api';
import useSideBarFunds from 'features/IRWorkstation/hooks/useFunds';

const IRWorkstationContext = createContext(null);

export const useIRWorkstation = () => {
	const context = useContext(IRWorkstationContext);
	if (!context) {
		throw new Error('useIRWorkstation must be used within IRWorkstationProvider');
	}
	return context;
};

export const IRWorkstationProvider = ({ children }) => {
	const { tenantId } = useParams();
	const [tenant, setTenant] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	// funds + selection are shared by the sidebar and the Active raise panel.
	// entities arrive ordered by CreatedDate DESC, so funds[0] is the
	// most-recently-created fund — the default selection.
	const { entities: funds, loading: fundsLoading, error: fundsError } =
		useSideBarFunds();
	const [selectedFundId, setSelectedFundId] = useState(null);

	useEffect(() => {
		if (!selectedFundId && funds?.length) {
			setSelectedFundId(funds[0].id);
		}
	}, [selectedFundId, funds]);

	useEffect(() => {
		const fetchTenant = async () => {
			if (!tenantId) {
				setLoading(false);
				return;
			}

			setLoading(true);
			setError(null);

			try {
				const data = await authedApiFetch({
					endpoint: `/tenants/${tenantId}`,
					method: 'GET',
				});
				setTenant(data);
			} catch (err) {
				setError('Failed to load tenant');
				console.error('Failed to fetch tenant:', err);
			} finally {
				setLoading(false);
			}
		};

		fetchTenant();
	}, [tenantId]);

	return (
		<IRWorkstationContext.Provider
			value={{
				tenant,
				loading,
				error,
				funds,
				fundsLoading,
				fundsError,
				selectedFundId,
				setSelectedFundId,
			}}
		>
			{children}
		</IRWorkstationContext.Provider>
	);
};

IRWorkstationProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export default IRWorkstationContext;
